/**
 * Native ↔ page protocol v1. The spec is `appclip/protocol/PROTOCOL.md`;
 * these types mirror it and change only together with it.
 */

export const PROTOCOL_VERSION = 1;

/** 16 numbers, column-major. */
export type Mat4 = readonly number[];

export interface HelloReply {
    v: number;
    immersiveAR: boolean;
}

export interface TrackedImageRequest {
    /** PNG, base64 without a data-URL prefix. */
    png: string;
    widthM: number;
}

export type ImageScore = 'trackable' | 'untrackable';

export interface RequestSessionReply {
    imageScores: ImageScore[];
}

export type PageRequest =
    | { type: 'hello'; v: number }
    | { type: 'requestSession'; images: TrackedImageRequest[] }
    | { type: 'endSession' };

export interface PageAck {
    type: 'rendered';
    /** The `t` of the native frame the page just drew. */
    t: number;
}

export interface FrameImage {
    index: number;
    /** World-from-image, in WebXR image space. */
    transform: Mat4;
    state: 'tracked' | 'emulated';
    widthM: number;
}

export interface FrameMessage {
    type: 'frame';
    t: number;
    tracking: 'normal' | 'limited' | 'none';
    /** `transform` is world-from-view; `projection`'s depth terms are ignored. */
    view: { transform: Mat4; projection: Mat4 };
    images: FrameImage[];
}

/** The page ignores a `type` it doesn't know, so new messages can be added. */
export type NativeMessage =
    | FrameMessage
    | { type: 'reset' }
    | { type: 'ended' };

/**
 * A reply native never sends is a native bug. Callers that must go on
 * without it (starting the app, leaving AR) bound the wait with this.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    let timer = 0;
    const timeout = new Promise<never>((_, reject) => { timer = window.setTimeout(() => reject(new Error('timeout')), ms); });
    return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
}

/** The two directions of the transport, so tests can stand in for native. */
export interface NativeHost {
    request(msg: PageRequest): Promise<unknown>;
    ack(msg: PageAck): void;
}
